// Keeps the last full response of endpoints that answer change checks
// (`ifNoneMatch` in the request, `etag` and `unchanged` in the response), so a
// reload can reuse it instead of downloading it again. Entries are keyed by
// user, endpoint and request body; the server's etag is a hash of the exact
// payload, so a match means the stored data is what the server would send.

const DATABASE_NAME = 'issen-conditional-responses';
const DATABASE_VERSION = 1;
const OBJECT_STORE = 'responses';

export type ConditionalResponse = {
  key: string;
  etag: string;
  data: unknown;
};

export class ConditionalResponseCache {
  private readonly memory = new Map<string, ConditionalResponse>();
  private databasePromise: Promise<IDBDatabase> | null = null;
  private generation = 0;

  constructor(private readonly indexedDb: IDBFactory | null) {}

  static keyFor(userId: string, url: string, body: Record<string, unknown>): string {
    return `${userId}\n${url}\n${JSON.stringify(body)}`;
  }

  // Changes on every clear; writes that started before a clear are dropped
  // so a signed-out account's data cannot come back.
  currentGeneration(): number {
    return this.generation;
  }

  async lookup(key: string): Promise<ConditionalResponse | null> {
    const cached = this.memory.get(key);
    if (cached !== undefined) {
      return cached;
    }
    const generation = this.generation;
    try {
      const database = await this.openDatabase();
      if (database === null) {
        return null;
      }
      const stored = await new Promise<ConditionalResponse | null>((resolve, reject) => {
        const request = database.transaction(OBJECT_STORE, 'readonly').objectStore(OBJECT_STORE).get(key);
        request.onsuccess = () => resolve((request.result as ConditionalResponse | undefined) ?? null);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
      });
      if (stored === null || generation !== this.generation) {
        return null;
      }
      this.memory.set(key, stored);
      return stored;
    } catch {
      // A missing or broken store only costs one full download.
      return null;
    }
  }

  async store(entry: ConditionalResponse, generation: number): Promise<void> {
    if (generation !== this.generation) {
      return;
    }
    this.memory.set(entry.key, entry);
    try {
      const database = await this.openDatabase();
      if (database === null || generation !== this.generation) {
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(OBJECT_STORE, 'readwrite');
        transaction.objectStore(OBJECT_STORE).put(entry);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB write failed'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB write aborted'));
      });
    } catch {
      // The in-memory copy still serves this page.
    }
  }

  async clear(): Promise<void> {
    this.generation += 1;
    this.memory.clear();
    try {
      const database = await this.openDatabase();
      if (database === null) {
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(OBJECT_STORE, 'readwrite');
        transaction.objectStore(OBJECT_STORE).clear();
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB clear failed'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB clear aborted'));
      });
    } catch {
      // Entries are keyed by user, so a leftover entry is never served to
      // another account.
    }
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (this.indexedDb === null) {
      return Promise.resolve(null);
    }
    if (this.databasePromise === null) {
      const indexedDb = this.indexedDb;
      this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDb.open(DATABASE_NAME, DATABASE_VERSION);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains(OBJECT_STORE)) {
            request.result.createObjectStore(OBJECT_STORE, { keyPath: 'key' });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
        request.onblocked = () => reject(new Error('IndexedDB open blocked'));
      });
    }
    return this.databasePromise;
  }
}

export const conditionalResponseCache = new ConditionalResponseCache(
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined' ? window.indexedDB : null,
);
